import {
  Box,
  Checkbox,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material';
import assert from 'assert';
import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CellProps,
  Column,
  HeaderGroup,
  IdType,
  Row,
  TableState,
  useExpanded,
  useFlexLayout,
  useGroupBy,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import { XsButton, XsIconButton, XsLoading } from '.';
import { XsOverflowCell } from './XsOverflowCell';
import queryClient from '../helpers/query';
import { IS_FO } from '../config/constants';
import ServerTableStore from '../stores/ServerTableStore';
import { TColumnExt } from '../types/types';

export interface ITableSelection<T extends object> {
  getRowId: (originalRow: T) => string;
  onSelectionChanged?: (rowIds: Array<string>) => void;
  isRowSelectable?: (originalRow: T) => boolean;
  /** @default 10 */
  colWidth?: number;
}

interface Props<T extends object> {
  name: string;
  control: 'client' | 'server';
  columns: TColumnExt<T>[];
  data: {
    rows: Array<T>;
    totalRows?: number;
  };

  onRowClick?: (row: T, data: T[]) => void;
  initState: Partial<TableState>;
  pagination: boolean;

  groupBy?: string;
  defaultRowsExpanded?: boolean;
  disabledSort?: boolean;
  onChangeState?: (name: string, state: TableState, callback: Function) => void;
  shouldRefresh?: number | undefined;
  refetchAfterDelete?: number | undefined;
  clearSelectionAfterDelete?: boolean;
  selection?: ITableSelection<T>;
  loading?: boolean;
  defaultSelectedRowIds?: Array<string>;
  disabledSelectedRowIds?: Array<string>;
  hideCheckboxRow?: (row: any) => boolean;
}

export const XsTableView = <T extends object>({
  name,
  control,
  columns,
  data,
  onRowClick,
  groupBy,
  defaultRowsExpanded,
  disabledSort,

  initState,
  onChangeState = ServerTableStore.applyTableState,
  shouldRefresh,
  refetchAfterDelete,
  clearSelectionAfterDelete,

  pagination,
  selection,
  loading = false,
  defaultSelectedRowIds = [],
  disabledSelectedRowIds = [],
  hideCheckboxRow
}: Props<T>) => {
  assert(!pagination || !groupBy, `Pagination is not allowed with grouping data in table '${name}'!`);

  const { getRowId, onSelectionChanged, isRowSelectable, colWidth = 10 } = selection ?? {};

  const { t } = useTranslation();

  const selectionColumn: Column<T> = {
    id: '_selection',
    width: colWidth,
    Header: () => {
      const isAllChecked =
        instance.page.length > 0 &&
        instance.page.every((x) => !(isRowSelectable?.(x.original) ?? true) || x.isSelected);
      const isSomeChecked = instance.page.some((x) => x.isSelected);
      return (
        <Checkbox
          checked={isAllChecked}
          indeterminate={!isAllChecked && isSomeChecked}
          onClick={(e) => {
            e.stopPropagation();
            for (const row of instance.page) {
              if (isRowSelectable?.(row.original) ?? true) {
                row.toggleRowSelected(!isAllChecked);
              }
            }
          }}
        />
      );
    },
    Cell: (cell: CellProps<any>) => {
      const row = cell.row;
      const isVisible = isRowSelectable?.(row.original) ?? true;

      let hideCheckbox = false;

      if (hideCheckboxRow) {
        hideCheckbox = hideCheckboxRow(row.original);
      }

      if (!isVisible || hideCheckbox) return null;

      return (
        <Checkbox
          checked={row.isSelected}
          disabled={
            disabledSelectedRowIds.length > 0 && row?.original?._id
              ? disabledSelectedRowIds.includes(row.original._id)
              : false
          }
          onClick={(e) => {
            e.stopPropagation();
            cell.row.toggleRowSelected();
          }}
        />
      );
    }
  };

  const rtColumns: Column<T>[] = useMemo(() => {
    if (!Boolean(selection)) return columns as Column<T>[];

    return [selectionColumn as Column<T>].concat(columns as Column<T>[]);
  }, [columns]);
  const manualControl = control === 'server';
  const totalRows = data.totalRows ?? data.rows.length;

  const paginationSettings = pagination
    ? {
        autoResetPage: false,
        manualPagination: manualControl,
        pageCount: Math.ceil(totalRows / (initState.pageSize ?? ServerTableStore.defaultPagination.pageSize))
      }
    : { manualPagination: true }; // ak je strankovanie vypnute zobrazuj vsetky data na jednu stranu

  const instance = useTable<T>(
    {
      columns: rtColumns,
      data: data.rows,
      initialState: { pageSize: IS_FO ? 10 : 20, ...initState, groupBy: [groupBy ?? ''] },
      disableSortBy: disabledSort ? true : false,

      ...paginationSettings,

      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetSelectedRows: false,

      manualSortBy: manualControl,

      getRowId
    },
    useFlexLayout,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    groupedRows,
    rows,
    page,
    prepareRow,
    nextPage,
    canNextPage,
    previousPage,
    canPreviousPage,
    setPageSize,
    setSortBy,
    pageCount,
    state: { pageIndex, pageSize },
    gotoPage
  } = instance;

  const clearSelection = () => {
    instance.state.selectedRowIds = {} as Record<IdType<T>, boolean>;
    instance.toggleAllRowsSelected(false);
  };

  useEffect(() => {
    gotoPage(ServerTableStore.getPagination(name).pageIndex);
    // Inicializuj default selectedRowIds
    if (defaultSelectedRowIds.length > 0) {
      const selectedRowIds: Record<string, boolean> = {};
      defaultSelectedRowIds.forEach((id) => {
        selectedRowIds[id] = true;
      });
      instance.state.selectedRowIds = selectedRowIds;
    } else {
      clearSelection();
    }
  }, [shouldRefresh]);

  useEffect(() => {
    if (pagination && refetchAfterDelete && pageIndex !== 0 && page.length === 0) {
      previousPage();
    }
    if (clearSelectionAfterDelete) {
      clearSelection();
    }
  }, [refetchAfterDelete]);

  useEffect(() => {
    if (Object.keys(initState?.expanded ?? {}).length === 0) {
      groupedRows.forEach((x) => {
        if (x.canExpand) x.toggleRowExpanded(defaultRowsExpanded);
      });
    } else {
      groupedRows.forEach((x) => {
        if (x.canExpand) x.toggleRowExpanded(Boolean(initState.expanded?.[x.id]));
      });
    }
  }, [groupedRows, defaultRowsExpanded]);

  const isMounted = useRef(false);
  const [pagerEnabled, setPagerEnabled] = useState(true);

  useEffect(() => {
    if (!isMounted.current) return;

    const expanded = instance.expandedRows
      .filter((row) => row.isGrouped)
      .reduce((acc, v) => ({ ...acc, [v.id]: Boolean(v.isExpanded) }), {});
    onChangeState?.(name, { ...instance.state, expanded }, () => setPagerEnabled(true));
  }, [instance.state.pageSize, instance.state.pageIndex, instance.state.sortBy, instance.state.expanded]);

  useEffect(() => {
    onSelectionChanged?.(Object.keys(instance.state.selectedRowIds));
  }, [instance.state.selectedRowIds]);

  const rowsFrom = pageIndex * pageSize + 1;
  const rowsTo = pageIndex * pageSize + pageSize > totalRows ? totalRows : pageIndex * pageSize + pageSize;

  useEffect(() => {
    if (!isMounted.current) return;

    if (control === 'client') {
      setSortBy(initState.sortBy ?? []);
    }
  }, [initState.sortBy]);

  const getSortIndicator = <T extends object>(column: HeaderGroup<T>) => {
    if (column.isSorted) {
      if (column.isSortedDesc) {
        return (
          <>
            {' '}
            <i className='fal fa-long-arrow-down'></i>
          </>
        );
      } else {
        return (
          <>
            {' '}
            <i className='fal fa-long-arrow-up'></i>
          </>
        );
      }
    } else {
      return null;
    }
  };

  const getSummaryRows = (row: Row<T>) => {
    const rtColumns = Boolean(selection) ? [selectionColumn as TColumnExt<T>].concat(columns) : columns;

    if (rtColumns.some((x) => x.summaries && x.summaries.length > 0)) {
      const summaries = rtColumns.find((x) => x.summaries && x.summaries.length > 0)?.summaries;

      const flatColumns = rtColumns.reduce(
        (acc: TColumnExt<T>[], v: any) => (acc = acc.concat(v.columns?.length > 0 ? v.columns : v)),
        []
      );

      return summaries?.map((sumRow, sumIdx) => {
        const isLastRow: boolean = summaries.length - 1 === sumIdx;

        return (
          <TableRow
            {...row.getRowProps()}
            key={`summaryRow_row_${sumIdx}`}
            className={classnames({ bgGreyLight: !isLastRow, bgGreySummary: isLastRow })}
          >
            {row.cells.map((cell, i) => {
              const col = flatColumns[i];
              const sumCell = (col?.summaries && col?.summaries?.[sumIdx]) ?? null;
              const { context = 'PAGE' } = sumCell || {};

              const data = context === 'PAGE' ? page : rows;

              const sumValue = sumCell?.value
                ? typeof sumCell.value === 'function'
                  ? sumCell?.value(data)
                  : sumCell.value
                : null;

              return (
                <TableCell
                  {...cell.getCellProps()}
                  className={classnames('bold', sumCell?.classNames, col.classNames, {
                    textRight: col.align === 'right',
                    textCenter: col.align === 'center'
                  })}
                >
                  {sumValue !== null
                    ? sumCell?.formatter && typeof sumValue === 'number'
                      ? sumCell.formatter(sumValue)
                      : sumValue
                    : ''}
                </TableCell>
              );
            })}
          </TableRow>
        );
      });
    }

    return null;
  };

  const renderTableCell = (cell: any, row: Row<T>, tooltip: string | JSX.Element | null) => {
    if (row.isGrouped) {
      if (cell.isGrouped) {
        return (
          <div {...row.getToggleRowExpandedProps()} style={{ padding: '10px' }}>
            {row.isExpanded ? (
              <i className='fas fa-caret-down fa-lg pr-1'></i>
            ) : (
              <i className='fas fa-caret-right fa-lg pr-1'></i>
            )}{' '}
            {cell.render('Cell')} ({row.subRows.length})
          </div>
        );
      } else {
        return (
          <div
            {...row.getToggleRowExpandedProps()}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '10px'
            }}
          >
            &nbsp;{cell.render('Aggregated') ?? cell.value}
          </div>
        );
      }
    }

    if (tooltip) {
      return (
        <Tooltip title={tooltip}>
          <div style={{ height: '100%' }} className={classnames('tooltiped-content')}>
            {cell.render('Cell')}
          </div>
        </Tooltip>
      );
    }

    return cell.isPlaceholder ? null : cell.render('Cell');
  };

  // On dismount invalidate all queries
  // Invalidate works only when cacheTime is set to 0
  useEffect(() => {
    isMounted.current = true;
    return () => {
      queryClient.invalidateQueries([name]);
    };
  }, []);

  const selectedRowsCount = useMemo(() => {
    return Object.keys(instance.state.selectedRowIds).length;
  }, [instance.state.selectedRowIds]);

  const renderSelectionInfo = () => {
    if (selectedRowsCount === 0) return null;

    return (
      <Grid container spacing={2} alignItems='center'>
        <Grid item>{`${t('table.selected_count')}: ${selectedRowsCount}`}</Grid>
        <Grid item>
          <XsButton label={t('table.clear_selection')} onClick={clearSelection} variant='outlined' />
        </Grid>
      </Grid>
    );
  };

  return (
    <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} className='bgGreyLight'>
              {headerGroup.headers.map((column) => {
                const col = column as TColumnExt<T>;
                return (
                  <TableCell
                    {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
                    className={classnames('preLine', col.classNames, {
                      textRight: col.align === 'right',
                      textCenter: col.align === 'center'
                    })}
                  >
                    {typeof column.Header === 'string' ? t(column.Header) : column.render('Header')}

                    {/* Add a sort direction indicator */}
                    {getSortIndicator(column)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {page.length > 0 ? (
            page.map((row, idx) => {
              prepareRow(row);
              return (
                <React.Fragment key={idx}>
                  <TableRow
                    {...row.getRowProps()}
                    className={classnames({
                      bgWhite: (groupBy && row.isGrouped) || (!groupBy && idx % 2 === 1)
                    })}
                  >
                    {row.cells.map((cell, i) => {
                      const column = cell.column as TColumnExt<T>;

                      const cellClick =
                        onRowClick && typeof onRowClick === 'function' && !column?.disableClick && !row.isGrouped;
                      const tooltip = column.Tooltip ? column.Tooltip(row) : null;

                      return (
                        <TableCell
                          {...cell.getCellProps()}
                          onClick={() => {
                            if (cellClick) {
                              if (window?.getSelection()?.toString()) {
                                return;
                              }
                              onRowClick?.(
                                row.original,
                                page.map((x) => x.original)
                              );
                              ServerTableStore.setSelectedRow(name, JSON.stringify(row.values));
                            }
                          }}
                          className={classnames('td', column.classNames, {
                            pointer: cellClick,
                            bgGreenLight: ServerTableStore.isSelectedRow(name, JSON.stringify(row.values)),
                            textRight: column?.align === 'right',
                            textCenter: column?.align === 'center',
                            'pa-0': row.isGrouped
                          })}
                        >
                          {column?.canOverflow ? (
                            <XsOverflowCell>{cell.render('Cell')}</XsOverflowCell>
                          ) : (
                            renderTableCell(cell, row, tooltip)
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {page.length === idx + 1 && getSummaryRows(row)}
                </React.Fragment>
              );
            })
          ) : (
            <Grid container justifyContent='center'>
              <Grid item fontWeight='500' paddingY={4}>
                {loading ? <XsLoading /> : t('table.no_data')}
              </Grid>
            </Grid>
          )}
        </TableBody>
      </Table>
      {!pagination && (
        <Box pt={1}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              {renderSelectionInfo()}
            </Grid>
          </Grid>
        </Box>
      )}
      {pagination && data.rows.length > 0 && (
        <Box pt={1}>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              {renderSelectionInfo()}
            </Grid>
            {!pagerEnabled && (
              <Grid item>
                <XsLoading size={25} />
              </Grid>
            )}
            <Grid item>{t('table.rows_per_page')}:</Grid>
            <Grid item>
              <TextField
                size='small'
                select
                value={pageSize}
                disabled={!pagerEnabled}
                onChange={(e) => {
                  setPagerEnabled(false);
                  setPageSize(Number(e.target.value));
                }}
              >
                <MenuItem key={10} value={10}>
                  10
                </MenuItem>
                <MenuItem key={20} value={20}>
                  20
                </MenuItem>
                <MenuItem key={50} value={50}>
                  50
                </MenuItem>
                <MenuItem key={100} value={100}>
                  100
                </MenuItem>
                <MenuItem key={500} value={500}>
                  500
                </MenuItem>
                <MenuItem key={1000} value={1000}>
                  1000
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item>
              {rowsFrom} - {rowsTo} {t('table.of')} {totalRows}
            </Grid>
            <Grid item>
              <XsIconButton
                size='small'
                icon={<i className='far fa-chevron-double-left' />}
                onClick={() => {
                  setPagerEnabled(false);
                  gotoPage(0);
                }}
                disabled={!canPreviousPage || !pagerEnabled}
                color='default'
              />
            </Grid>
            <Grid item>
              <XsIconButton
                size='small'
                icon={<i className='far fa-chevron-left' />}
                onClick={() => {
                  setPagerEnabled(false);
                  previousPage();
                }}
                disabled={!canPreviousPage || !pagerEnabled}
                color='default'
              />
            </Grid>
            <Grid item>
              <XsIconButton
                size='small'
                icon={<i className='far fa-chevron-right' />}
                onClick={() => {
                  setPagerEnabled(false);
                  nextPage();
                }}
                disabled={!canNextPage || !pagerEnabled}
                color='default'
              />
            </Grid>
            <Grid item>
              <XsIconButton
                size='small'
                icon={<i className='far fa-chevron-double-right' />}
                onClick={() => {
                  setPagerEnabled(false);
                  gotoPage(pageCount - 1);
                }}
                disabled={!canNextPage || !pagerEnabled}
                color='default'
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </div>
  );
};
